import { useEffect, useRef, useState } from 'react';
import {
  Checkbox,
  DefaultButton,
  Dropdown,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  Stack,
  Text,
  TextField,
} from '@fluentui/react';
import {
  getAutomaticSurcharges,
  saveAutomaticSurcharge,
  type IAutomaticSurchargeRule,
  type ISaveAutomaticSurcharge,
} from '../../services/automaticSurchargeService';
import {
  getPricingItems,
  type IPricingItem,
} from '../../services/pricingService';
import { getApiErrorMessage } from '@moko/utils';

const empty = (pricingItemId = 0): ISaveAutomaticSurcharge => ({
  pricingItemId,
  enabled: false,
  countryCode: null,
  stateCode: null,
});
export default function AutomaticSurcharges() {
  const [rules, setRules] = useState<IAutomaticSurchargeRule[]>([]);
  const [items, setItems] = useState<IPricingItem[]>([]);
  const [rule, setRule] = useState<ISaveAutomaticSurcharge>(empty());
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [reload, setReload] = useState(0);
  const pending = useRef<AbortController | undefined>(undefined);
  useEffect(() => {
    const controller = new AbortController();
    setLoaded(false);
    setError(undefined);
    const catalog = async () => {
      const first = await getPricingItems(controller, 500, 1);
      const rows = [...first.items];
      for (let page = 2; page <= first.pageCount; page++)
        rows.push(...(await getPricingItems(controller, 500, page)).items);
      return rows;
    };
    Promise.all([getAutomaticSurcharges(controller), catalog()])
      .then(([stored, all]) => {
        if (controller.signal.aborted) return;
        setRules(stored);
        setItems(
          all.filter(
            (item) =>
              !item.isDeleted &&
              item.calculationMethod === 'Percentage' &&
              item.chargeRole === 'AdditionalCharge' &&
              item.quantityType === 'Fixed'
          )
        );
        setRule(stored[0] ?? empty());
        setLoaded(true);
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(getApiErrorMessage(e, 'Unable to load surcharge defaults.'));
      });
    return () => {
      controller.abort();
      pending.current?.abort();
    };
  }, [reload]);
  const change = (patch: Partial<ISaveAutomaticSurcharge>) => {
    setRule((current) => ({ ...current, ...patch }));
    setSaved(false);
    setError(undefined);
  };
  const options = items.map((item) => ({
    key: item.id,
    text: `${item.name} (${item.code})`,
  }));
  rules
    .filter((entry) => !items.some((item) => item.id === entry.pricingItemId))
    .forEach((entry) =>
      options.push({
        key: entry.pricingItemId,
        text: `${entry.name} (${entry.code}) — unavailable`,
      })
    );
  const geographic = rule.countryCode !== null;
  const valid =
    rule.pricingItemId > 0 &&
    (!geographic || /^[A-Za-z]{2}$/.test(rule.countryCode ?? '')) &&
    (!rule.stateCode || /^[A-Za-z0-9]{1,10}$/.test(rule.stateCode));
  const disabled = !loaded || saving;
  const save = async () => {
    const controller = new AbortController();
    pending.current = controller;
    setSaving(true);
    setSaved(false);
    setError(undefined);
    try {
      const input = {
        pricingItemId: rule.pricingItemId,
        enabled: rule.enabled,
        countryCode: rule.countryCode?.trim().toUpperCase() || null,
        stateCode: rule.stateCode?.trim().toUpperCase() || null,
      };
      await saveAutomaticSurcharge(controller, input);
      const current = await getAutomaticSurcharges(controller);
      if (controller.signal.aborted) return;
      setRules(current);
      setRule(
        current.find((entry) => entry.pricingItemId === input.pricingItemId) ??
          input
      );
      setSaved(true);
    } catch (e) {
      if (!controller.signal.aborted)
        setError(getApiErrorMessage(e, 'Unable to save surcharge default.'));
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  };
  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: 720 } }}>
      <Text variant="large">Automatic surcharges</Text>
      <Text>
        Set the default for all tenants. Each tenant can inherit this default or
        override it in Pricing → Automatic surcharges. Manage percentages and
        effective dates in Pricing rates.
      </Text>
      {!loaded && !error && <Spinner label="Loading surcharge defaults…" />}
      {error && (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      )}
      {!loaded && error && (
        <DefaultButton text="Retry" onClick={() => setReload((n) => n + 1)} />
      )}
      {saved && (
        <MessageBar messageBarType={MessageBarType.success}>
          Surcharge default saved.
        </MessageBar>
      )}
      <Dropdown
        label="Surcharge pricing item"
        required
        placeholder="Select a percentage pricing item"
        options={options}
        selectedKey={rule.pricingItemId || undefined}
        disabled={disabled}
        onChange={(_, option) => {
          if (option) {
            setRule(
              rules.find((entry) => entry.pricingItemId === option.key) ??
                empty(Number(option.key))
            );
            setSaved(false);
            setError(undefined);
          }
        }}
      />
      {loaded && items.length === 0 && (
        <Text>
          Create a fixed Percentage AdditionalCharge pricing item first.
        </Text>
      )}
      <Checkbox
        label="Enabled by default"
        checked={rule.enabled}
        disabled={disabled || !rule.pricingItemId}
        onChange={(_, checked) => change({ enabled: !!checked })}
      />
      <Text>
        Tenants using the global setting follow this default. Explicit tenant
        overrides take precedence, including when the global default is
        disabled.
      </Text>
      <Checkbox
        label="Restrict to Site location"
        checked={geographic}
        disabled={disabled || !rule.pricingItemId}
        onChange={(_, checked) =>
          change({ countryCode: checked ? '' : null, stateCode: null })
        }
      />
      {geographic && (
        <>
          <TextField
            label="Country code"
            required
            value={rule.countryCode ?? ''}
            maxLength={2}
            description="Two-letter country code, for example US."
            disabled={disabled}
            onChange={(_, value) => change({ countryCode: value ?? '' })}
            errorMessage={
              !/^[A-Za-z]{2}$/.test(rule.countryCode ?? '')
                ? 'Enter a two-letter country code.'
                : undefined
            }
          />
          <TextField
            label="State / province code"
            value={rule.stateCode ?? ''}
            maxLength={10}
            description="Optional. For California use country US and state CA."
            disabled={disabled}
            onChange={(_, value) => change({ stateCode: value || null })}
          />
          <Text>
            Site conditions apply to every enabled tenant, including tenant
            overrides. The surcharge applies once when any linked Site matches.
          </Text>
        </>
      )}
      <Stack horizontal>
        <PrimaryButton
          text={saving ? 'Saving…' : 'Save default'}
          disabled={disabled || !valid}
          onClick={save}
        />
      </Stack>
    </Stack>
  );
}
